/**
 * TryoutView – Cold-Email Tryout Funnel (empty-first redesign, v2)
 *
 * URL: /tryout/:token
 * Flow:
 *   Step 1: Upload (own image or preset)
 *   Step 2: Leerräumen — generate 2 empty-room variants (A/B), pick one,
 *           optionally refine it (up to MAX_REFINES times)
 *   Step 3: Ergebnis — pick a staging style, generate/reveal the staged
 *           result (watermarked preview + signup CTA)
 *
 * Token economy (see src/lib/tryout/tryoutRun.ts): one token use covers an
 * entire run — the first empty generation, all its refines, and its staging.
 * Presets with pre-baked assets (manifest.json) don't consume a token at all
 * unless a live AI call becomes unavoidable (missing bake, or a refine).
 */

import { useCallback, useEffect, useState } from 'react';
import { XCircleIcon, SparklesIcon, ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { track, identifyTryoutLead, gateTryoutPageView } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';
import { resolvePersonalization } from '@/lib/tryout/personalizationResolver';
import {
  initialRunState,
  newRunId,
  callConsumesToken,
  canRefine,
  MAX_REFINES,
} from '@/lib/tryout/tryoutRun';
import type { EmptyVariant, TryoutRunState } from '@/lib/tryout/tryoutRun';
import {
  bakedEmptyUrl,
  bakedStagedUrl,
  needsLiveStaging,
} from '@/lib/tryout/presetManifest';
import type { PresetManifest } from '@/lib/tryout/presetManifest';
import { rowsToManifest, type PresetAssetRow } from '@/lib/tryout/presetAssets';
import { generateEmpty, generateStage, refineEmpty, TryoutApiError } from '@/lib/tryout/tryoutApi';
import { Navbar } from '@/components/landing/Navbar';
import { FooterSection } from '@/components/landing/FooterSection';
import { SocialProofBar } from '@/components/landing/SocialProofBar';
import { FeaturesSection } from '@/components/landing/FeaturesSection';
import { HowItWorksSection } from '@/components/landing/HowItWorksSection';
import { PricingSection } from '@/components/landing/PricingSection';
import { ROISection } from '@/components/landing/ROISection';
import { TestimonialsSection } from '@/components/landing/TestimonialsSection';
import { FAQSection } from '@/components/landing/FAQSection';
import '@/components/landing/landing.css';
import { TryoutProgressBar } from '@/components/tryout/TryoutProgressBar';
import { TryoutStep1Upload, PRESET_IMAGES } from '@/components/tryout/TryoutStep1Upload';
import { TryoutStep2Configure } from '@/components/tryout/TryoutStep2Configure';
import { TryoutStep3Results } from '@/components/tryout/TryoutStep3Results';
import type { PresetImage } from '@/components/tryout/TryoutStep1Upload';
import { resolvePresets } from '@/lib/tryout/presetOverrides';
import type { PresetOverrideRow } from '@/lib/tryout/presetOverrides';
import type { AspectRatio, StagingStyle } from '@/components/tryout/TryoutStep2Configure';
import type { BoxCoordinates } from '@/types';

// ============================================================================
// Types
// ============================================================================

interface TryoutViewProps {
  token: string;
}

interface TokenInfo {
  tokenId: string | null;
  usageCount: number;
  maxUses: number;
  leadFirstName: string | null;
  leadName: string | null;
  leadCompany: string | null;
  personalizationDisplayed: string | null;
}

type FunnelStep = 'upload' | 'empty' | 'result';

interface EmptyVariantState {
  variant: EmptyVariant;
  base64: string | null; // null for a not-yet-fetched baked preset asset
  url: string;
}

// ============================================================================
// Helpers
// ============================================================================

const MAX_DIMENSION = 1500;

// Minimum time the generating stage (USP bubbles + progress) stays visible, so
// the visitor always has time to read the features — even when a baked preset
// resolves instantly. Real AI generations already exceed this, so this only
// adds a wait on the instant paths.
const MIN_GENERATING_MS = 20000;

// How long the "Fertig!" completion flourish plays before the result is revealed.
const FINISH_ANIM_MS = 1100;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const waitMinDisplay = async (startedAt: number): Promise<void> => {
  const remaining = MIN_GENERATING_MS - (Date.now() - startedAt);
  if (remaining > 0) await sleep(remaining);
};

async function resizeImageToBase64(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const scale = MAX_DIMENSION / Math.max(width, height);
        width  = Math.round(width  * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = (reader.result as string).split(',')[1];
            resolve({ base64, mimeType: 'image/jpeg' });
          };
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.92,
      );
    };

    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

async function fetchPresetAsBase64(src: string): Promise<{ base64: string; mimeType: string }> {
  const response = await fetch(src);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64  = dataUrl.split(',')[1];
      const mime    = blob.type || 'image/jpeg';
      resolve({ base64, mimeType: mime });
    };
    reader.onerror = () => reject(new Error('Failed to read preset image'));
    reader.readAsDataURL(blob);
  });
}

function base64ToUrl(b64: string): string {
  const bytes = new Uint8Array(atob(b64).split('').map((c) => c.charCodeAt(0)));
  return URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
}

// Only `base64ToUrl` output are blob: URLs we own — preset bakes and the
// upload preview URL are not ours to revoke here.
function revokeBlobUrl(url: string | null | undefined): void {
  if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
}

function extractFirstName(fullName: string | null): string | null {
  if (!fullName?.trim()) return null;
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const startIdx = (['herr', 'frau'].includes(parts[0]?.toLowerCase())) ? 1 : 0;
  return parts[startIdx] ?? null;
}

const DEFAULT_REGENERATE_REASON =
  'Große Änderung — lieber neu leerräumen?';

// ============================================================================
// Main Component
// ============================================================================

export const TryoutView: React.FC<TryoutViewProps> = ({ token }) => {

  // ── Token state ─────────────────────────────────────────────────────────────
  const [tokenInfo,      setTokenInfo]      = useState<TokenInfo | null>(null);
  const [tokenError,     setTokenError]     = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(true);

  // ── Image selection ──────────────────────────────────────────────────────────
  const [selectedFile,    setSelectedFile]    = useState<File | null>(null);
  const [previewUrl,      setPreviewUrl]      = useState<string | null>(null);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [isDragOver,      setIsDragOver]      = useState(false);

  // ── Generation settings ──────────────────────────────────────────────────────
  const [roomType,    setRoomType]    = useState('living_room');
  // Aspect ratio is fixed for the tryout (the picker was removed); the empty +
  // staging generations always use 4:3.
  const [aspectRatio] = useState<AspectRatio>('4:3');
  const [stagingStyle, setStagingStyle] = useState<StagingStyle>('modern');
  // Kitchen built-ins are kept by default; the toggle was removed with the
  // config step (a preset's room type already controls this server-side).
  const [keepKitchen] = useState(true);

  // ── Preset manifest (pre-baked assets) ───────────────────────────────────────
  const [manifest, setManifest] = useState<PresetManifest | null>(null);
  const [effectivePresets, setEffectivePresets] = useState<PresetImage[]>(PRESET_IMAGES);
  // Gates the preset grid: don't render preset thumbnails (with their hardcoded
  // default srcs) until admin overrides have resolved, otherwise an overridden
  // preset flashes its old default image before the replacement loads.
  const [overridesLoaded, setOverridesLoaded] = useState(false);

  // ── Run state (empty → refine → stage) ───────────────────────────────────────
  const [runState,      setRunState]      = useState<TryoutRunState>(initialRunState());
  const [emptyVariants, setEmptyVariants] = useState<EmptyVariantState[]>([]);
  const [isGenerating,  setIsGenerating]  = useState(false);
  const [isRefining,    setIsRefining]    = useState(false);
  const [isStaging,     setIsStaging]     = useState(false);
  // True during the brief completion flourish shown right before a result reveal.
  const [isFinishing,   setIsFinishing]   = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [refineError,     setRefineError]     = useState<string | null>(null);
  const [stagingError,    setStagingError]    = useState<string | null>(null);
  const [stagedUrl,       setStagedUrl]       = useState<string | null>(null);
  const [stagedBase64,    setStagedBase64]    = useState<string | null>(null);
  const [limitReached,    setLimitReached]    = useState(false);

  // ── Funnel step ───────────────────────────────────────────────────────────────
  const [funnelStep, setFunnelStep] = useState<FunnelStep>('upload');

  // ============================================================================
  // Analytics
  // ============================================================================

  // Fire 'Tryout Page Viewed' only when the visitor actually interacts (mouse
  // move / touch / scroll / key) or stays >2s. Cuts Microsoft Safe Links and
  // Outlook scanner hits that would otherwise pollute Mixpanel. The same call
  // also writes server-side via track-email-click (UA/IP bot filter + DB log).
  useEffect(() => {
    return gateTryoutPageView({ token });
  }, [token]);

  // ============================================================================
  // Load the pre-baked preset manifest once, from the active DB rows. A
  // failure (query error, network) is treated as "no bakes available" —
  // every preset then falls back to a live generation, same as an upload.
  // ============================================================================

  useEffect(() => {
    let cancelled = false;
    const toUrl = (p: string) => supabase.storage.from('images').getPublicUrl(p).data.publicUrl;
    (async () => {
      try {
        const { data } = await supabase
          .from('tryout_preset_assets')
          .select('preset_id, kind, variant, style, storage_path')
          .eq('is_active', true);
        if (!cancelled) setManifest(rowsToManifest((data ?? []) as PresetAssetRow[], toUrl));
      } catch {
        if (!cancelled) setManifest({});
      }

      // Preset overrides drive the effective label/source/room. Fetched
      // separately so a manifest failure can't skip it — and `overridesLoaded`
      // always flips true so the preset grid can render (with correct srcs).
      try {
        const { data: overrides } = await supabase
          .from('tryout_preset_overrides')
          .select('preset_id, label, source_path, source_updated_at, room');
        if (!cancelled && overrides) {
          setEffectivePresets(resolvePresets(PRESET_IMAGES, overrides as PresetOverrideRow[], toUrl));
        }
      } catch {
        // keep hardcoded defaults
      } finally {
        if (!cancelled) setOverridesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ============================================================================
  // Load token info
  // ============================================================================

  useEffect(() => {
    let cancelled = false;

    const isPreviewMode =
      import.meta.env.DEV &&
      (token === 'preview' || new URLSearchParams(window.location.search).get('preview') === '1');

    if (isPreviewMode) {
      setTokenInfo({
        tokenId: null,
        usageCount: 0,
        maxUses: 3,
        leadFirstName: 'Max',
        leadName: 'Max Mustermann',
        leadCompany: 'Musterfirma',
        personalizationDisplayed: 'Bei Musterfirma in Berlin lassen sich Exposés in Minuten erstellen — Estage.ai zeigt euch direkt wie.',
      });
      setIsLoadingToken(false);
      return;
    }

    const loadToken = async () => {
      setIsLoadingToken(true);
      setTokenError(null);

      const { data, error } = await supabase
        .from('tryout_tokens')
        .select(`
          id, usage_count, max_uses, is_active, expires_at,
          lead_first_name, crm_lead_name, crm_lead_company, crm_lead_email,
          location, bundesland, berufsgruppe,
          list_id, campaign_name, source,
          personalization_original, personalization_ai_generated,
          personalization_custom, personalization_use
        `)
        .eq('token', token)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setTokenError('Ungültiger oder abgelaufener Zugangscode.');
      } else if (!data.is_active) {
        setTokenError('Dieser Link ist nicht mehr aktiv.');
      } else if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setTokenError('Dieser Link ist abgelaufen.');
      } else {
        const personalizationDisplayed = resolvePersonalization({
          personalization_original: data.personalization_original ?? null,
          personalization_ai_generated: data.personalization_ai_generated ?? null,
          personalization_custom: data.personalization_custom ?? null,
          personalization_use: data.personalization_use ?? null,
          berufsgruppe: data.berufsgruppe ?? null,
          location: data.location ?? null,
        });
        setTokenInfo({
          tokenId:      data.id ?? null,
          usageCount:   data.usage_count,
          maxUses:      data.max_uses,
          leadFirstName: data.lead_first_name ?? null,
          leadName:     data.crm_lead_name  ?? null,
          leadCompany:  data.crm_lead_company ?? null,
          personalizationDisplayed,
        });

        // Identify the cold-email lead in Mixpanel so all activity on this
        // device session (including the earlier 'Tryout Page Viewed') is
        // attributed to the actual person, plus register super-properties
        // for filtering by company / list / campaign / Berufsgruppe.
        if (data.crm_lead_email) {
          identifyTryoutLead({
            email:         data.crm_lead_email,
            firstName:     data.lead_first_name ?? null,
            fullName:      data.crm_lead_name ?? null,
            company:       data.crm_lead_company ?? null,
            location:      data.location ?? null,
            bundesland:    data.bundesland ?? null,
            berufsgruppe:  data.berufsgruppe ?? null,
            list_id:       data.list_id ?? null,
            campaign_name: data.campaign_name ?? null,
            source:        data.source ?? null,
            token,
          });
          track('Tryout Lead Recognized', {
            email:         data.crm_lead_email,
            company:       data.crm_lead_company,
            list_id:       data.list_id,
            campaign_name: data.campaign_name,
            berufsgruppe:  data.berufsgruppe,
            city:          data.location,
          });
        }

        if (data.usage_count >= data.max_uses) {
          setLimitReached(true);
        }
      }
      setIsLoadingToken(false);
    };

    loadToken();
    return () => { cancelled = true; };
  }, [token]);

  // ============================================================================
  // Image handling
  // ============================================================================

  const resetRun = useCallback(() => {
    emptyVariants.forEach((v) => revokeBlobUrl(v.url));
    revokeBlobUrl(stagedUrl);
    setRunState(initialRunState());
    setEmptyVariants([]);
    setStagedUrl(null);
    setStagedBase64(null);
    setGenerationError(null);
    setRefineError(null);
    setStagingError(null);
  }, [emptyVariants, stagedUrl]);

  const clearImage = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setSelectedPresetId(null);
    resetRun();
  }, [resetRun]);

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    setSelectedFile(file);
    setSelectedPresetId(null);
    resetRun();
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    track('Tryout Image Selected', { source: 'upload', room_type: roomType });
  }, [roomType, resetRun]);

  const handlePresetSelect = useCallback((preset: PresetImage) => {
    setSelectedFile(null);
    setSelectedPresetId(preset.id);
    setRoomType(preset.room);
    resetRun();
    setPreviewUrl(preset.src);
    track('Tryout Image Selected', { source: 'preset', preset_id: preset.id, room_type: preset.room });
  }, [resetRun]);

  useEffect(() => {
    return () => {
      // Only object-URL blobs (from a real file upload) need revoking. Preset
      // sources — hardcoded or admin-overridden http(s) URLs — must never be
      // passed to revokeObjectURL.
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  // ============================================================================
  // Step 2 — Leerräumen: generate the empty-room A/B variants
  // ============================================================================

  const handleGenerateEmpty = useCallback(async () => {
    if (isGenerating || !previewUrl) return;

    const isPresetSource = !!selectedPresetId;
    const manifestSafe = manifest ?? {};
    const bakedA = isPresetSource ? bakedEmptyUrl(manifestSafe, selectedPresetId!, 'A') : null;
    const bakedB = isPresetSource ? bakedEmptyUrl(manifestSafe, selectedPresetId!, 'B') : null;
    const bothBaked = !!bakedA && !!bakedB;

    // A preset with both A/B bakes never consumes a token — safe to show
    // even if the token's free tries are exhausted. Everything else (an
    // upload, or a preset missing a bake) needs a real AI call.
    if (!bothBaked && callConsumesToken(runState, isPresetSource ? 'preset' : 'upload', 'empty') && limitReached) {
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    const startedAt = Date.now();
    const runId = newRunId();

    if (bothBaked) {
      emptyVariants.forEach((v) => revokeBlobUrl(v.url));
      // Keep the generating stage (USP bubbles) up for the minimum time even
      // though the baked variants are ready instantly, then play the completion
      // flourish before revealing.
      await waitMinDisplay(startedAt);
      setIsFinishing(true);
      await sleep(FINISH_ANIM_MS);
      setRunState({ runId, tokenConsumed: false, phase: 'empty', selectedVariant: null, refineCount: 0 });
      setEmptyVariants([
        { variant: 'A', base64: null, url: bakedA! },
        { variant: 'B', base64: null, url: bakedB! },
      ]);
      setIsFinishing(false);
      setIsGenerating(false);
      track('Tryout Empty Generated', { source: 'preset', variant_count: 2 });
      return;
    }

    try {
      const { base64, mimeType } = selectedFile
        ? await resizeImageToBase64(selectedFile)
        : await fetchPresetAsBase64(previewUrl);

      const data = await generateEmpty({
        token, runId, source: isPresetSource ? 'preset' : 'upload',
        imageBase64: base64, mimeType, roomType, keepKitchen, aspectRatio,
      });

      await waitMinDisplay(startedAt);
      setIsFinishing(true);
      await sleep(FINISH_ANIM_MS);

      emptyVariants.forEach((v) => revokeBlobUrl(v.url));
      setEmptyVariants(data.emptyVariants.map((v) => ({
        variant: v.variant, base64: v.base64, url: base64ToUrl(v.base64),
      })));
      setRunState({ runId, tokenConsumed: true, phase: 'empty', selectedVariant: null, refineCount: 0 });

      const newRemaining = data.remaining ?? 0;
      setTokenInfo((prev) => (prev ? { ...prev, usageCount: prev.maxUses - newRemaining } : prev));
      if (newRemaining === 0) setLimitReached(true);

      track('Tryout Empty Generated', { source: isPresetSource ? 'preset' : 'upload', variant_count: 2 });
    } catch (err) {
      if (err instanceof TryoutApiError && err.code === 'limit_reached') {
        setLimitReached(true);
        setTokenInfo((prev) => (prev ? { ...prev, usageCount: prev.maxUses } : prev));
      }
      setGenerationError(err instanceof Error ? err.message : 'Generierung fehlgeschlagen');
    } finally {
      setIsGenerating(false);
      setIsFinishing(false);
    }
  }, [isGenerating, previewUrl, selectedPresetId, manifest, runState, limitReached, selectedFile, token, roomType, keepKitchen, aspectRatio, emptyVariants]);

  // ============================================================================
  // Step 2 — A/B selection (+ model-selection analytics log)
  // ============================================================================

  const handleSelectVariant = useCallback((v: EmptyVariant) => {
    setRunState((prev) => ({ ...prev, selectedVariant: v }));
    track('Tryout Variant Selected', { variant: v, room_type: roomType });
    // Fire-and-forget A/B choice log. tokenId can be null for a not-yet-
    // materialised preset run (preview mode, or manifest still loading) —
    // that's an accepted gap, not a blocker.
    void supabase.from('model_selections').insert({
      user_id: null,
      tryout_token_id: tokenInfo?.tokenId ?? null,
      model_selected: 'gemini-3.1-flash-image',
      generation_type: 'Empty',
      room_type: roomType,
      prompt_variant: v,
    });
  }, [roomType, tokenInfo]);

  // ============================================================================
  // Step 2 — Refine the chosen empty variant
  // ============================================================================

  const handleRefine = useCallback(async (userPrompt: string, box?: BoxCoordinates) => {
    if (isRefining || !canRefine(runState) || !runState.runId || !runState.selectedVariant) return;

    const isPresetSource = !!selectedPresetId;
    if (callConsumesToken(runState, isPresetSource ? 'preset' : 'upload', 'refine') && limitReached) {
      setRefineError('Keine kostenlosen Versuche mehr übrig.');
      return;
    }

    setIsRefining(true);
    setRefineError(null);

    try {
      const selected = emptyVariants.find((v) => v.variant === runState.selectedVariant);
      if (!selected) throw new Error('Kein Bild ausgewählt');

      let emptyBase64 = selected.base64;
      let mimeType = 'image/png';
      if (!emptyBase64) {
        const fetched = await fetchPresetAsBase64(selected.url);
        emptyBase64 = fetched.base64;
        mimeType = fetched.mimeType;
      }

      const data = await refineEmpty({
        token, runId: runState.runId, emptyBase64, mimeType, roomType, userPrompt, boxCoordinates: box,
      });

      const newRemaining = data.remaining;
      setTokenInfo((prev) => (prev ? { ...prev, usageCount: prev.maxUses - newRemaining } : prev));
      if (newRemaining === 0) setLimitReached(true);

      if (data.regenerate_suggested) {
        const reason = data.regenerate_reason ?? DEFAULT_REGENERATE_REASON;
        setRefineError(reason);
        // The server already bumped refine_count before short-circuiting.
        setRunState((prev) => ({ ...prev, tokenConsumed: true, refineCount: prev.refineCount + 1 }));
        track('Tryout Refine Regenerate Suggested', { reason });
        return;
      }

      const refinedBase64 = data.refinedBase64;
      const selectedVariant = runState.selectedVariant;
      if (refinedBase64) {
        setEmptyVariants((prev) => prev.map((v) => {
          if (v.variant !== selectedVariant) return v;
          revokeBlobUrl(v.url);
          return { ...v, base64: refinedBase64, url: base64ToUrl(refinedBase64) };
        }));
      }

      const newRefineCount = data.refineCount ?? runState.refineCount + 1;
      setRunState((prev) => ({ ...prev, tokenConsumed: true, refineCount: newRefineCount }));
      track('Tryout Refine Used', { refine_index: newRefineCount, has_box: !!box });
    } catch (err) {
      if (err instanceof TryoutApiError && err.code === 'limit_reached') {
        setLimitReached(true);
      }
      setRefineError(err instanceof Error ? err.message : 'Anpassung fehlgeschlagen');
    } finally {
      setIsRefining(false);
    }
  }, [isRefining, runState, selectedPresetId, limitReached, emptyVariants, token, roomType]);

  // ============================================================================
  // Step 3 — Create the staged result (baked lookup or live generation)
  // ============================================================================

  const handleCreateStaging = useCallback(async () => {
    if (isStaging || !runState.selectedVariant) return;

    const isPresetSource = !!selectedPresetId;
    const baked = isPresetSource
      ? bakedStagedUrl(manifest ?? {}, selectedPresetId!, runState.selectedVariant, stagingStyle)
      : null;
    const live = needsLiveStaging({
      isPreset: isPresetSource,
      wasRefined: runState.refineCount > 0,
      bakedUrl: baked,
    });

    if (live && limitReached && callConsumesToken(runState, isPresetSource ? 'preset' : 'upload', 'stage')) {
      setStagingError('Keine kostenlosen Versuche mehr übrig.');
      return;
    }

    setIsStaging(true);
    setStagingError(null);

    const startedAt = Date.now();

    try {
      if (!live && baked) {
        revokeBlobUrl(stagedUrl);
        // Hold the generating stage for the minimum time even for instant bakes,
        // then play the completion flourish before revealing.
        await waitMinDisplay(startedAt);
        setIsFinishing(true);
        await sleep(FINISH_ANIM_MS);
        setStagedUrl(baked);
        setStagedBase64(null);
        track('Tryout Staging Created', { staging_style: stagingStyle, from: 'baked' });
      } else {
        const selected = emptyVariants.find((v) => v.variant === runState.selectedVariant);
        if (!selected) throw new Error('Kein Bild ausgewählt');

        let emptyBase64 = selected.base64;
        let mimeType = 'image/png';
        if (!emptyBase64) {
          const fetched = await fetchPresetAsBase64(selected.url);
          emptyBase64 = fetched.base64;
          mimeType = fetched.mimeType;
        }

        const data = await generateStage({
          token, runId: runState.runId!, source: isPresetSource ? 'preset' : 'upload',
          imageBase64: emptyBase64, mimeType, roomType, aspectRatio, stagingStyle,
        });

        await waitMinDisplay(startedAt);
        setIsFinishing(true);
        await sleep(FINISH_ANIM_MS);

        revokeBlobUrl(stagedUrl);
        setStagedUrl(base64ToUrl(data.stagedBase64));
        setStagedBase64(data.stagedBase64);
        setRunState((prev) => ({ ...prev, tokenConsumed: true, phase: 'stage' }));

        const newRemaining = data.remaining ?? 0;
        setTokenInfo((prev) => (prev ? { ...prev, usageCount: prev.maxUses - newRemaining } : prev));
        if (newRemaining === 0) setLimitReached(true);

        track('Tryout Staging Created', { staging_style: stagingStyle, from: 'live' });
      }
    } catch (err) {
      if (err instanceof TryoutApiError && err.code === 'limit_reached') {
        setLimitReached(true);
      }
      setStagingError(err instanceof Error ? err.message : 'Einrichten fehlgeschlagen');
    } finally {
      setIsStaging(false);
      setIsFinishing(false);
    }
  }, [isStaging, runState, selectedPresetId, manifest, stagingStyle, limitReached, emptyVariants, token, roomType, aspectRatio, stagedUrl]);

  // ============================================================================
  // Reset & Signup
  // ============================================================================

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setSelectedPresetId(null);
    resetRun();
    setFunnelStep('upload');
  }, [resetRun]);

  const handleSignup = useCallback(() => {
    track('Tryout Signup Clicked', { from_step: 'result' });
    // /signup opens the landing register modal (signup tab + "10 Credits
    // gratis" banner) — the conversion intent, not the sign-in page.
    window.location.href = '/signup';
  }, []);

  const handleNavCta = useCallback(() => {
    track('Tryout NavCTA Clicked');
    window.location.href = '/signup';
  }, []);

  const handleNavLogin = useCallback(() => {
    track('Tryout NavLogin Clicked');
    window.location.href = '/login';
  }, []);

  // ============================================================================
  // Derived
  // ============================================================================

  const usagesLeft  = tokenInfo ? tokenInfo.maxUses - tokenInfo.usageCount : null;
  const firstName   = tokenInfo ? (tokenInfo.leadFirstName ?? extractFirstName(tokenInfo.leadName)) : null;
  const companyName = tokenInfo?.leadCompany ?? null;
  const selectedEmptyUrl = emptyVariants.find((v) => v.variant === runState.selectedVariant)?.url ?? null;

  // A preset with BOTH empties baked never consumes a token — free to show
  // even once the visitor's real token count hits 0. Everything else
  // (an upload, or a preset missing a bake) needs a real AI call and must
  // stay gated on remaining tokens.
  const isPreset = !!selectedPresetId;
  const emptyWillConsume = !(
    isPreset &&
    bakedEmptyUrl(manifest ?? {}, selectedPresetId!, 'A') &&
    bakedEmptyUrl(manifest ?? {}, selectedPresetId!, 'B')
  );
  const canGenerateEmpty = !emptyWillConsume || usagesLeft !== 0;

  // Emptying auto-starts the moment the visitor reaches step 2 — there is no
  // upfront config screen anymore. Guarded so it fires once, never loops on
  // error, and never runs when the free tries are used up (canGenerateEmpty).
  useEffect(() => {
    if (
      funnelStep === 'empty' &&
      previewUrl &&
      canGenerateEmpty &&
      !isGenerating &&
      emptyVariants.length === 0 &&
      !generationError
    ) {
      void handleGenerateEmpty();
    }
  }, [funnelStep, previewUrl, canGenerateEmpty, isGenerating, emptyVariants.length, generationError, handleGenerateEmpty]);

  // ============================================================================
  // Render: loading token
  // ============================================================================

  if (isLoadingToken) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
        <ArrowPathIcon className="w-7 h-7 text-[#FE6F17] animate-spin" />
      </div>
    );
  }

  if (tokenError) {
    return (
      <div
        className="min-h-screen bg-[#F5F4F0] flex items-center justify-center p-6"
        style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
      >
        <div className="text-center max-w-sm bg-white rounded-[20px] p-8 shadow-sm border border-[#EBEBE6]">
          <div className="w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-7 h-7 text-[#B91C1C]" strokeWidth={1.5} />
          </div>
          <h2 className="text-[20px] text-[#001116] mb-2" style={{ fontWeight: 600 }}>
            Ungültiger Link
          </h2>
          <p className="text-[14px] text-[#5F6B70]">{tokenError}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: full page
  // ============================================================================

  const headline = firstName
    ? `${firstName}, teste das KI-Staging kostenlos`
    : 'Teste das KI-Staging kostenlos';

  return (
    <div className="landing-root">
      {/* Real Navbar from landing page */}
      <Navbar onCtaClick={handleNavCta} onLoginClick={handleNavLogin} />

      {/* Hero text */}
      <section
        className="tryout-hero"
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: '120px 24px 0',
          textAlign: 'center',
        }}
      >
        {firstName && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 14px',
              marginBottom: 18,
              borderRadius: 999,
              background: '#FFF3EC',
              color: '#FE6F17',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <SparklesIcon style={{ width: 16, height: 16 }} strokeWidth={1.5} />
            Exklusiv für {firstName}{companyName ? ` · ${companyName}` : ''}
          </div>
        )}
        <h1
          style={{
            fontSize: 'clamp(30px, 5vw, 48px)',
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: '-0.005em',
            color: '#001116',
            margin: '0 0 14px',
            fontFamily: "'Outfit Variable', 'Outfit', sans-serif",
          }}
        >
          {headline}
        </h1>
        <p
          style={{
            fontSize: 17,
            fontWeight: 400,
            color: '#5F6B70',
            lineHeight: 1.55,
            maxWidth: 640,
            margin: '0 auto',
          }}
        >
          {tokenInfo?.personalizationDisplayed
            ?? (companyName
              ? `Exklusiv für ${companyName} – in 30 Sekunden sehen, wie deine Immobilienfotos transformiert aussehen.`
              : 'Sehe in 30 Sekunden, wie dein Immobilienfoto transformiert aussieht.')}
        </p>
      </section>

      {/* Main funnel card — the Leerräumen + Ergebnis steps lay the original and
          the result (or the side-by-side generating stage) out next to each
          other, so they get a wider frame on desktop for a bigger image. */}
      <section
        style={{
          maxWidth: funnelStep === 'upload' ? 1000 : funnelStep === 'empty' ? 1320 : 1200,
          margin: '40px auto 0',
          padding: '0 24px 80px',
          transition: 'max-width 0.3s ease',
        }}
      >
        <div
          style={{
            background: '#FCFAF5',
            borderRadius: 20,
            border: '1px solid #EBEBE6',
            boxShadow: '0 4px 6px -2px rgba(0,0,0,0.03), 0 12px 16px -4px rgba(0,0,0,0.08)',
            padding: '32px 32px',
            fontFamily: "'Outfit Variable', 'Outfit', sans-serif",
          }}
        >
          <TryoutProgressBar step={funnelStep} />

          {funnelStep === 'upload' && (
            <TryoutStep1Upload
              previewUrl={previewUrl}
              selectedPresetId={selectedPresetId}
              isDragOver={isDragOver}
              onFileSelect={processFile}
              onPresetSelect={handlePresetSelect}
              onClearImage={clearImage}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onNext={() => setFunnelStep('empty')}
              remaining={usagesLeft}
              presets={effectivePresets}
              overridesReady={overridesLoaded}
              roomType={roomType}
              onRoomTypeChange={setRoomType}
            />
          )}

          {funnelStep === 'empty' && previewUrl && (
            <TryoutStep2Configure
              previewUrl={previewUrl}
              aspectRatio={aspectRatio}
              hasGenerated={emptyVariants.length > 0}
              isGenerating={isGenerating}
              isFinishing={isFinishing}
              canGenerate={canGenerateEmpty}
              emptyVariants={emptyVariants}
              selectedVariant={runState.selectedVariant}
              refinesLeft={MAX_REFINES - runState.refineCount}
              isRefining={isRefining}
              generationError={generationError}
              refineError={refineError}
              onGenerateEmpty={handleGenerateEmpty}
              onSelectVariant={handleSelectVariant}
              onRefine={handleRefine}
              onNext={() => setFunnelStep('result')}
              onBack={() => setFunnelStep('upload')}
            />
          )}

          {funnelStep === 'result' && previewUrl && selectedEmptyUrl && (
            <TryoutStep3Results
              previewUrl={previewUrl}
              emptyUrl={selectedEmptyUrl}
              stagedUrl={stagedUrl}
              stagedBase64={stagedBase64}
              stagingStyle={stagingStyle}
              roomType={roomType}
              aspectRatio={aspectRatio}
              isStaging={isStaging}
              isFinishing={isFinishing}
              stagingError={stagingError}
              remaining={usagesLeft}
              limitReached={limitReached}
              onStyleChange={setStagingStyle}
              onCreateStaging={handleCreateStaging}
              onReset={handleReset}
              onSignup={handleSignup}
            />
          )}
        </div>
      </section>

      {/* Rest of the landing page — single source of truth, reused via component imports */}
      <SocialProofBar />
      <FeaturesSection />
      <HowItWorksSection onCtaClick={handleNavCta} />
      <PricingSection onCtaClick={handleNavCta} />
      <ROISection />
      <TestimonialsSection onCtaClick={handleNavCta} />
      <FAQSection />

      {/* ── Final CTA ─────────────────────────────────────────────────────────── */}
      <section style={{ background: 'rgba(254, 111, 23, 0.04)', borderTop: '1px solid #EBEBE6' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '56px 24px', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(18px, 3vw, 26px)', fontWeight: 600, color: '#001116', margin: '0 0 12px', fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
            Bereit, deine Immobilienfotos zu transformieren?
          </h2>
          <p style={{ fontSize: 14, color: '#5F6B70', margin: '0 0 20px' }}>
            Registriere dich jetzt und erhalte{' '}
            <span style={{ fontWeight: 700, color: '#FE6F17' }}>10 Credits gratis</span>.
          </p>
          <a
            href="/signup"
            onClick={() => track('Tryout Signup Clicked', { location: 'footer_cta' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 28px', background: '#FE6F17', color: '#fff',
              border: '2px solid #FFD4BF',
              borderRadius: 10, fontWeight: 600, fontSize: 14,
              textDecoration: 'none',
              fontFamily: "'Outfit Variable', 'Outfit', sans-serif",
            }}
          >
            Jetzt kostenlos testen
            <ArrowUpRightIcon style={{ width: 16, height: 16 }} strokeWidth={1.75} />
          </a>
          <p style={{ fontSize: 12, color: '#9CA3AF', margin: '16px 0 0' }}>
            Made &amp; Hosted in Germany
          </p>
        </div>
      </section>

      {/* Real footer from landing page */}
      <FooterSection />
    </div>
  );
};
